import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  RatingList,
  BatchDetail,
  BatchFilter,
  BatchesModal,
  BatchListing,
  RatingFilter,
  RatingDetails,
  BlockChainDetails,
  UploadBatchListing,
  AssociatedStrainList,
  TokenizedBatchListing,
  RatingBatchDetailsProps,
  BatchListingQueryParameters,
  RatingListingQueryParameters,
  UploadBatchListingQueryParameters,
  AssociatedStrainListingQueryParameters,
  ConsumerList,
  ConsumerFilter,
  ConsumerListingQueryParameters,
  ConsumerBatchDetails,
  ProductCollectedListing,
  ProductCollectedListingQueryParameters,
  ProductTokenizationDraft,
} from "./types";

const initialState: BatchesModal = {
  headerNameListing: [],

  uploadBatchListing: {
    data: [],
    total: 0,
    pageNo: 0,
    totalPage: 0,
    nextHit: 0,
    limit: 0,
  },

  uploadBatchListingQueryParameters: {
    pageNo: 1,
    limit: 10,
  },

  batchListing: {
    data: [],
    total: 0,
    pageNo: 0,
    totalPage: 0,
    nextHit: 0,
    limit: 0,
  },

  batchListingQueryParameters: {
    pageNo: 1,
    limit: 10,
    searchKey: "",
  },

  batchDetail: {
    _id: "",
    batchId: "",
    sampleId: "",
    clientId: "",
    orderStatusId: "",
    regulatorBatchId: "",
    regulatorSampleId: "",
    manufacturerId: "",
    sampleName: "",
    isProductTokenized: false,
    isCoaTokenized: false,
    status: "",
    product: {
      type: "",
      subType: "",
      image: "",
    },
    strain: {
      name: "",
      strainId: "",
      mongoId: "",
      description: "",
    },
    dataPlatform: "",
    isDeprecated: false,
    createdAt: "",
    harvestDate: "",
    packageDate: "",
    coaStatus: "",
    coaNftViewUrl: "",
    pdfFileIpfsHash: "",
    testedDate: "",
    growerNote: "",
    qrCodeProcessComplete: false,
    isDownload: false,
    productTokenizedAt: "",
    coaMintTxnId: "",
    brandNumbers: [],
    scans: 0,
    isArchived: false,
    collections: 0,
    isBatchTypeUpdated: false,
    viewProductOnChainUrl: "",
    productMintTxnId: "",
    reviewCalculationData: {
      totalProductCount: 0,
      totalRatingCount: 0,
    },
    totalProductTokenizationCount: 0,
    qrCodeData: {
      _id: "",
      startingSerialNumber: "",
      endingSerialNumber: "",
      rollQuantity: 0,
      generatedBy: "",
      operationId: "",
      processStatus: "",
      priorityCheck: "",
      type: "",
      operationType: "",
      totalLabels: 0,
      fringeLabel: 0,
      batchId: "",
      addedBy: "",
      manufacturerId: "",
      created: 0,
      createdAt: "",
      updatedAt: "",
    },
  },

  ratingList: {
    data: [],
    total: 0,
    pageNo: 0,
    totalPage: 0,
    nextHit: 0,
    limit: 0,
  },

  ratingListingQueryParameters: {
    pageNo: 1,
    limit: 10,
  },

  ratingDetails: {
    _id: "",
    consumerClaimId: "",
    batchIndexId: "",
    rating: {
      look: 0,
      smell: 0,
      taste: 0,
      strength: 0,
      averageRating: 0,
    },
    review: "",
    status: "",
    isPublic: false,
    created: 0,
    createdAt: "",
    consumerId: "",
    consumerDetails: {
      _id: "",
      profilePicture: "",
      name: "",
    },
  },
  consumerList: {
    data: [],
    total: 0,
    pageNo: 0,
    totalPage: 0,
    nextHit: 0,
    limit: 0,
  },

  consumerListDetails: {
    _id: "",
    serialNumber: "",
    updatedAt: "",
    username: "",
    consumerId: "",
    reviewGiven: false,
    mfgCollection: "",
  },

  consumerListingQueryParameters: {
    pageNo: 1,
    limit: 10,
  },

  consumerFilter: {
    sortBy: 0,
    sortCriteria: "",
    reviewSubmitted: undefined,
  },

  ratingBatchDetailsProps: {
    _id: "",
    consumerClaimId: "",
    batchIndexId: "",
    rating: {
      look: 0,
      smell: 0,
      taste: 0,
      strength: 0,
      averageRating: 0,
    },
    review: "",
    isPublic: false,
    created: 0,
    createdAt: "",
    consumerId: "",
    consumerDetails: {
      _id: "",
      profilePicture: "",
      name: "",
    },
    scratchOffCode: "",
    qrSerialNumberId: "",
    serialNumber: "",
    status: "", // defaulting to empty string, can be "ACTIVE"/"INACTIVE" later
    updatedAt: "",
    batchDetails: {
      _id: "",
      batchId: "",
      product: {
        typeId: "",
        subTypeId: "",
        type: "",
        subType: "",
        image: "",
        totalProductCount: "",
      },
      strain: {
        mongoId: "",
        name: "",
        strainId: "",
      },
    },
    claimData: {
      _id: "",
      created: 0,
      createdAt: "",
      batchIndexId: "",
    },
    manufacturerName: "",
    firstBatchManufacturerDate: "",
    topCollectorPercent: 0,
    topCollectorRank: 0,
    totalBrandCollectors: 0,
    topCollectorLabel: "",
  },
  ratingFilter: {
    sortBy: 0,
    sortCriteria: "",
    filterType: "",
  },

  consumerBatchDetails: {
    _id: "",
    name: "",
    brandName: "",
    walletAddress: "",
    topCollectorPercent: 0,
    topCollectorRank: 0,
    totalBrandCollectors: 0,
    quantity: 0,
    profilePicture: "",
    manufactureName: "",
    mostRecentProductCollected: "",
    mostCollectedProductType: "",
    firstProductCollectedDate: "",
    totalProductsCollected: 0,
    totalProductsReviewed: 0,
  },
  licenseListing: [],

  category: [],
  associatedStrainList: [],

  associatedStrainListingQueryParameters: {
    searchKey: "",
  },

  productCollectedListing: {
    data: [],
    total: 0,
    pageNo: 0,
    totalPage: 0,
    nextHit: 0,
    limit: 0,
  },

  productCollectedListingQueryParameters: {
    limit: 10,
    pageNo: 1,
  },

  productCollectedListingDetail: {
    _id: "",
    serialNumber: "",
    createdAt: "",

    manufacturer: {
      _id: "",
      companyName: "",
    },

    product: {
      _id: "",
      batchId: "",
      strainName: "",
      strainMongoId: "",
      type: "",
    },

    rating: {
      look: 0,
      smell: 0,
      taste: 0,
      strength: 0,
      averageRating: 0,
    },
    averageRating: 0,

    hasRatingAndReview: false,
  },

  importedBatchId: "",

  draftDetail: null,

  tokenizedBatchListing: {
    data: [],
    total: 0,
    pageNo: 0,
    totalPage: 0,
    nextHit: 0,
    limit: 0,
  },

  batchFilter: {
    strain: [],
    status: [],
    productType: [],
    subType: [],
    coaTokenize: [],
  },

  strainList: [],

  typeList: [],

  subtypeList: [],

  blockChainDetails: {
    productImage: "",
    strainName: "",
    lineage: [],
    growerNote: "",
    coaPdfIpfsHash: "",
    dataPlatform: "",
    manufacturerInfo: {
      name: "",
      companyName: "",
      _id: "",
      companyPicture: "",
    },
    blockchainInfo: {
      tokenStandard: "",
      chainName: "",
      contractAddress: "",
      contractAddressRedirectUrl: "",
      tokenId: "",
      productNftMetadataIpfsUrl: "",
    },
    primaryInfo: {
      name: "",
      batchId: "",
      batchIndexId: "",
      productType: "",
      productSubType: "",
      totalProductCount: "",
      harvestDate: "",
      packageDate: "",
      cultivationState: "",
      cultivatorName: "",
    },
    keyComponents: {
      topThreeCannabinoids: [],
      topThreeTerpenes: [],
      thcTotal: "",
      cbdTotal: "",
      cannabinoidTotal: "",
      terpeneTotal: "",
    },
    maxCbdWeight: "",
    maxThcWeight: "",
    coaRawIpfsHash: "",
    metaDataIpfsHash: "",
    viewProductOnChainUrl: "",
    totalCannabinoidsWeight: "",
    totalTerpenesPercentWeight: "",
  },
};

const BatchesSlice = createSlice({
  name: "batches",
  initialState,
  reducers: {
    setHeaderNameListing: (state: BatchesModal, action: PayloadAction<[]>) => {
      state.headerNameListing = [...action.payload];
    },

    setLicenseNameListing: (state: BatchesModal, action: PayloadAction<[]>) => {
      state.licenseListing = [...action.payload];
    },

    setUploadBatchListing: (
      state: BatchesModal,
      action: PayloadAction<UploadBatchListing>,
    ) => {
      state.uploadBatchListing = action.payload;
    },

    setUploadBatchListingQueryParameters: (
      state: BatchesModal,
      action: PayloadAction<UploadBatchListingQueryParameters>,
    ) => {
      state.uploadBatchListingQueryParameters = action.payload;
    },

    setBatchListing: (
      state: BatchesModal,
      action: PayloadAction<BatchListing>,
    ) => {
      state.batchListing = action.payload;
    },

    setBatchDetail: (
      state: BatchesModal,
      action: PayloadAction<BatchDetail>,
    ) => {
      state.batchDetail = action.payload;
    },

    resetBatchDetail: (state: BatchesModal) => {
      state.batchDetail = initialState.batchDetail;
    },

    setRatingListingData: (
      state: BatchesModal,
      action: PayloadAction<RatingList>,
    ) => {
      state.ratingList = { ...action.payload };
    },

    setRatingListingQueryParameters: (
      state: BatchesModal,
      action: PayloadAction<RatingListingQueryParameters>,
    ) => {
      state.ratingListingQueryParameters = action.payload;
    },

    setRatingDetails: (
      state: BatchesModal,
      action: PayloadAction<RatingDetails>,
    ) => {
      state.ratingDetails = action.payload;
    },

    setRatingBatchDetailsProps: (
      state: BatchesModal,
      action: PayloadAction<RatingBatchDetailsProps>,
    ) => {
      state.ratingBatchDetailsProps = action.payload;
    },
    setRatingFilter: (
      state: BatchesModal,
      action: PayloadAction<Partial<RatingFilter>>,
    ) => {
      state.ratingFilter = {
        ...state.ratingFilter,
        ...action.payload,
      };
    },

    setConsumerBatchDetails: (
      state: BatchesModal,
      action: PayloadAction<ConsumerBatchDetails>,
    ) => {
      state.consumerBatchDetails = action.payload;
    },

    setSortByCriteria: (
      state: BatchesModal,
      action: PayloadAction<
        string | "A-Z" | "Z-A" | "New to Old" | "Old to New"
      >,
    ) => {
      switch (action.payload) {
        case "A-Z":
          state.ratingFilter = {
            ...state.ratingFilter,
            sortCriteria: "NAME",
            sortBy: 1,
            filterType: "",
          };
          break;
        case "Z-A":
          state.ratingFilter = {
            ...state.ratingFilter,
            sortCriteria: "NAME",
            sortBy: -1,
            filterType: "",
          };
          break;
        case "New to Old":
          state.ratingFilter = {
            ...state.ratingFilter,
            sortCriteria: "NEW_TO_OLD",
            sortBy: -1,
            filterType: "",
          };
          break;
        case "Old to New":
          state.ratingFilter = {
            ...state.ratingFilter,
            sortCriteria: "NEW_TO_OLD",
            sortBy: 1,
            filterType: "",
          };
          break;
        case "RATING_ONLY":
          state.ratingFilter = {
            ...state.ratingFilter,
            sortBy: 0,
            sortCriteria: "",
            filterType: "RATING_ONLY",
          };
          break;
        case "HAS_WRITTEN_REVIEW":
          state.ratingFilter = {
            ...state.ratingFilter,
            sortBy: 0,
            sortCriteria: "",
            filterType: "HAS_WRITTEN_REVIEW",
          };
          break;
        default:
          break;
      }
    },

    setTokenizedBatchListing: (
      state: BatchesModal,
      action: PayloadAction<TokenizedBatchListing>,
    ) => {
      state.tokenizedBatchListing = action.payload;
    },

    setBatchListingQueryParameters: (
      state: BatchesModal,
      action: PayloadAction<BatchListingQueryParameters>,
    ) => {
      state.batchListingQueryParameters = action.payload;
    },

    setCategoryListing: (state: BatchesModal, action: PayloadAction<[]>) => {
      state.category = [...action.payload];
    },

    setAssociatedStrainListing: (
      state: BatchesModal,
      action: PayloadAction<AssociatedStrainList[]>,
    ) => {
      state.associatedStrainList = [...action.payload];
    },

    setAssociatedStrainListingQueryParameters: (
      state: BatchesModal,
      action: PayloadAction<AssociatedStrainListingQueryParameters>,
    ) => {
      state.associatedStrainListingQueryParameters = action.payload;
    },

    setBatchFilter: (
      state: BatchesModal,
      action: PayloadAction<BatchFilter>,
    ) => {
      state.batchFilter = action.payload;
    },

    setStrainListing: (
      state: BatchesModal,
      action: PayloadAction<string[]>,
    ) => {
      state.strainList = [...action.payload];
    },

    setProductTypeListing: (
      state: BatchesModal,
      action: PayloadAction<string[]>,
    ) => {
      state.typeList = [...action.payload];
    },

    setBlockChainDetails: (
      state: BatchesModal,
      action: PayloadAction<BlockChainDetails>,
    ) => {
      state.blockChainDetails = action.payload;
    },

    setSubTypeListing: (
      state: BatchesModal,
      action: PayloadAction<string[]>,
    ) => {
      state.subtypeList = [...action.payload];
    },

    setConsumerListingData: (
      state: BatchesModal,
      action: PayloadAction<ConsumerList>,
    ) => {
      state.consumerList = { ...action.payload };
    },

    setConsumerListingQueryParameters: (
      state: BatchesModal,
      action: PayloadAction<ConsumerListingQueryParameters>,
    ) => {
      state.consumerListingQueryParameters = action.payload;
    },

    setConsumerFilter: (
      state: BatchesModal,
      action: PayloadAction<Partial<ConsumerFilter>>,
    ) => {
      state.consumerFilter = {
        ...state.consumerFilter,
        ...action.payload,
      };
    },

    setProductCollectedListing: (
      state: BatchesModal,
      action: PayloadAction<ProductCollectedListing>,
    ) => {
      state.productCollectedListing = action.payload;
    },

    setProductCollectedListingQueryParameters: (
      state: BatchesModal,
      action: PayloadAction<ProductCollectedListingQueryParameters>,
    ) => {
      state.productCollectedListingQueryParameters = action.payload;
    },

    // Realtime updates: set COA tokenization flags by batch Mongo _id
    setCoaTokenizationForBatch: (
      state: BatchesModal,
      action: PayloadAction<{
        batchMongoId: string;
        isCoaTokenized: boolean;
        coaTokenizedAt?: string;
      }>,
    ) => {
      const { batchMongoId, isCoaTokenized, coaTokenizedAt } = action.payload;
      const current = state.batchListing?.data || [];
      state.batchListing.data = current.map((b: any) => {
        const matches =
          b?._id === batchMongoId || b?.batchMongoId === batchMongoId;
        if (!matches) return b;
        return {
          ...b,
          isCoaTokenized,
          coaTokenizedAt: coaTokenizedAt ?? (b as any).coaTokenizedAt,
          // Optionally clear pending txn flag once tokenized
          coaMintTxnId: isCoaTokenized
            ? (b as any).coaMintTxnId || ""
            : (b as any).coaMintTxnId,
        };
      });
    },

    setDraftDetail: (
      state: BatchesModal,
      action: PayloadAction<ProductTokenizationDraft | null>,
    ) => {
      state.draftDetail = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setBatchFilter,
  setBatchDetail,
  setRatingFilter,
  setBatchListing,
  setStrainListing,
  setRatingDetails,
  resetBatchDetail,
  setSubTypeListing,
  setSortByCriteria,
  setCategoryListing,
  setHeaderNameListing,
  setBlockChainDetails,
  setRatingListingData,
  setUploadBatchListing,
  setProductTypeListing,
  setLicenseNameListing,
  setTokenizedBatchListing,
  setRatingBatchDetailsProps,
  setAssociatedStrainListing,
  setCoaTokenizationForBatch,
  setBatchListingQueryParameters,
  setRatingListingQueryParameters,
  setUploadBatchListingQueryParameters,
  setAssociatedStrainListingQueryParameters,
  setConsumerListingData,
  setConsumerListingQueryParameters,
  setConsumerFilter,
  setConsumerBatchDetails,
  setProductCollectedListing,
  setProductCollectedListingQueryParameters,
  setDraftDetail,
} = BatchesSlice.actions;

export default BatchesSlice.reducer;
